import { useRef, useCallback } from 'react';

const SWIPE_THRESHOLD = 50; // minimum horizontal px to count as a swipe

/**
 * Detects horizontal swipe gestures for pagination.
 * Only fires when horizontal movement exceeds vertical (avoids interfering with scroll).
 * Returns touch event handlers to attach to a container element.
 */
export function useSwipePagination(
  currentPage: number,
  totalPages: number,
  onPageChange: (page: number) => void
) {
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  }, []);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    const deltaX = e.changedTouches[0].clientX - touchStartX.current;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;

    // Only trigger if horizontal movement exceeds vertical
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > SWIPE_THRESHOLD) {
      if (deltaX < 0 && currentPage < totalPages) {
        // Swipe left → next page
        onPageChange(currentPage + 1);
      } else if (deltaX > 0 && currentPage > 1) {
        // Swipe right → previous page
        onPageChange(currentPage - 1);
      }
    }
  }, [currentPage, totalPages, onPageChange]);

  return { onTouchStart, onTouchEnd };
}
